import type { Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, principalKey } from '../http/keys.ts'

export type Capability = Schemas['LedgerSummaryResponse']['capabilities'][number]
export type LedgerRole = Schemas['LedgerSummaryResponse']['role']

export interface Principal {
  readonly subject: string
  readonly kind: Schemas['PrincipalResponse']['kind']
  /** Installation axis: may this principal ever do X. */
  readonly authorities: readonly string[]
}

export interface LedgerSummary {
  readonly id: string
  readonly name: string
  readonly role: LedgerRole
  /** Ledger axis, resolved server-side. Never re-derive it from the role. */
  readonly capabilities: readonly Capability[]
  readonly archivedAt: string | undefined
  /** Per (user, ledger) - the split-expense screen's one payment account (myWealth §4.2). */
  readonly defaultPaymentAccountId: string | undefined
  /** The five-character code other ledgers connect with. Visible to every member. */
  readonly shareCode: string
}

export interface LedgerFromBackup {
  readonly id: string
  readonly name: string
  readonly restored: Schemas['BackupRestoreResult']
}

export type StarterChartTemplate = Schemas['CreateLedgerRequest']['starterChartTemplate']

export const sessionKeys = {
  principal: (): CacheKey => principalKey('me'),
  ledgers: (): CacheKey => principalKey('ledgers'),
} as const

export interface SessionRepository {
  principal(signal?: AbortSignal): Promise<Principal>
  ledgers(signal?: AbortSignal): Promise<readonly LedgerSummary[]>
  /** 204 No Content; must be class ASSET and belong to `ledgerId` (checked server-side). */
  setDefaultPaymentAccount(ledgerId: string, accountId: string): Promise<void>
  /** The caller becomes OWNER; returns the new ledger's id. */
  createLedger(name: string, starterChartTemplate: StarterChartTemplate): Promise<string>
  /** Creates the ledger and restores `backup` into it in one atomic server call: on failure
   * no ledger is left behind. */
  createLedgerFromBackup(name: string, backup: File): Promise<LedgerFromBackup>
  /** 204 No Content. Owner-only. Ledgers are never deleted, only disabled; a disabled ledger
   * refuses every `{ledgerId}` call except `restoreLedger`. */
  disableLedger(ledgerId: string): Promise<void>
  /** 204 No Content. Owner-only; the one `{ledgerId}` call a disabled ledger still accepts. */
  restoreLedger(ledgerId: string): Promise<void>
}

export function createSessionRepository(client: TalerClient): SessionRepository {
  return {
    async principal(signal) {
      const response = await client.call<Schemas['PrincipalResponse']>({
        method: 'GET',
        path: '/api/v1/me',
        ...(signal === undefined ? {} : { signal }),
      })
      return { subject: response.subject, kind: response.kind, authorities: response.authorities }
    },

    async ledgers(signal) {
      const response = await client.call<Schemas['LedgerSummaryResponse'][]>({
        method: 'GET',
        path: '/api/v1/ledgers',
        ...(signal === undefined ? {} : { signal }),
      })
      return response.map((ledger) => ({
        id: ledger.id,
        name: ledger.name,
        role: ledger.role,
        capabilities: ledger.capabilities,
        archivedAt: ledger.archivedAt,
        defaultPaymentAccountId: ledger.defaultPaymentAccountId,
        shareCode: ledger.shareCode,
      }))
    },

    async setDefaultPaymentAccount(ledgerId, accountId) {
      await client.call<undefined, Schemas['SetDefaultPaymentAccountRequest']>({
        method: 'PUT',
        path: `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/default-payment-account`,
        body: { accountId },
      })
    },

    async createLedger(name, starterChartTemplate) {
      const response = await client.call<Schemas['LedgerResponse'], Schemas['CreateLedgerRequest']>({
        method: 'POST',
        path: '/api/v1/ledgers',
        body: { name, starterChartTemplate },
      })
      return response.id
    },

    async createLedgerFromBackup(name, backup) {
      const formData = new FormData()
      formData.append('name', name)
      formData.append('backup', backup, backup.name)
      const path = '/api/v1/ledgers/from-backup'
      const idempotencyPayload = {
        name,
        filename: backup.name,
        size: backup.size,
        lastModified: backup.lastModified,
      }
      const response = await client.callMultipart<Schemas['RestoredLedgerResponse']>({
        method: 'POST',
        path,
        formData,
        idempotencyPayload,
      })
      client.idempotencyKeys.forget({ method: 'POST', path, query: null, body: idempotencyPayload })
      return { id: response.id, name: response.name, restored: response.restored }
    },

    async disableLedger(ledgerId) {
      const path = `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/disable`
      const body: Record<string, never> = {}
      await client.call<undefined, Record<string, never>>({ method: 'POST', path, body })
      // Disable and restore send the same empty body every time, so a content-addressed key
      // would replay the first 204 for every later toggle. Forgetting it after success is what
      // `IdempotencyKeys.forget` exists for, the same as `ImportsRepository.editPreviewEntry`.
      client.idempotencyKeys.forget({ method: 'POST', path, query: null, body })
    },

    async restoreLedger(ledgerId) {
      const path = `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/restore`
      const body: Record<string, never> = {}
      await client.call<undefined, Record<string, never>>({ method: 'POST', path, body })
      client.idempotencyKeys.forget({ method: 'POST', path, query: null, body })
    },
  }
}
